import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { readCartResponse, showCartError, lockCart, restoreCartQuantity } from './cart-feedback.js';

// Fix bfcache : au retour navigateur, la page est restaurée depuis le cache
// (back-forward cache) avec le bouton « Passer commande » encore en is-loading
// -> spinner infini. On retire is-loading a chaque affichage de page. Le
// selecteur ne cible que les boutons reellement en chargement (no-op sinon).
if (typeof window !== 'undefined' && !window.__otwsCheckoutSpinnerReset) {
  window.__otwsCheckoutSpinnerReset = true;
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('[data-ref="checkout-button"].is-loading').forEach(function (btn) {
      btn.classList.remove('is-loading');
    });
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);

    this.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    const button = event.target.closest('[data-ref="checkout-button"]');
    if (button && this.contains(button) && !button.disabled) button.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  renderCartContents(parsedState) {
    const section_cart_drawer_id = this.dataset.sectionId;

    // Sécurité : si la section panier n'est pas dans la réponse, on ne casse rien.
    const newHtml = parsedState && parsedState.sections ? parsedState.sections[section_cart_drawer_id] : null;
    if (!newHtml) return;

    const currentCartDrawer = this.querySelector('.cart-drawer__inner');
    const newCartDrawer = new DOMParser().parseFromString(newHtml, 'text/html');
    const newInner = newCartDrawer.querySelector('.cart-drawer__inner');
    if (!currentCartDrawer || !newInner) return;

    // IMPORTANT : resynchroniser l'etat du tiroir depuis le rendu serveur.
    // Sinon, apres un ajout depuis un panier VIDE, la classe « --split » et la
    // largeur restent figees sur l'etat vide initial -> layout casse, articles
    // invisibles (cause du bug d'ajout depuis la home / panier vide).
    const newRoot = newCartDrawer.querySelector('cart-drawer');
    if (newRoot) {
      this.classList.toggle('otws-cart-drawer--split', newRoot.classList.contains('otws-cart-drawer--split'));
      this.classList.toggle('otws-cart--soldes', newRoot.classList.contains('otws-cart--soldes'));
      const newWidth = newRoot.style.getPropertyValue('--cart-drawer-width');
      if (newWidth) this.style.setProperty('--cart-drawer-width', newWidth);
    }

    currentCartDrawer.innerHTML = newInner.innerHTML;

    setTimeout(() => {
      if (parsedState.itemCount == 0) {
        this.classList.add('cart-drawer--empty');
      } else {
        this.classList.remove('cart-drawer--empty');
      }
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');
    this.confirmedQuantity = this.quantitySelectorInput?.value;

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  async #updateLineItemQuantity(line, quantity) {
    const drawer = this.closest('cart-drawer');
    const unlock = lockCart(drawer);
    if (!unlock) return;
    this.#enableLoading();
    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: Number(quantity), sections: drawer.dataset.sectionId, sections_url: window.location.pathname }),
      });
      const cart = await readCartResponse(response);
      if (!cart.sections?.[drawer.dataset.sectionId]) {
        // Shopify applied the change but did not render the drawer: show the real cart.
        window.location.assign(Theme.routes.cart_url);
        return;
      }
      document.dispatchEvent(new CartUpdatedEvent({}, {
        itemCount: cart.item_count,
        source: 'cart-drawer-item',
        sections: cart.sections,
      }));
    } catch (error) {
      restoreCartQuantity(this.quantitySelectorInput, this.confirmedQuantity);
      showCartError(error);
    } finally {
      this.#disableLoading();
      unlock();
    }
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
